import { queryClient } from './queryClient';
import { getAuthToken } from './supabase';

const SYNC_LOCK_KEY = 'offlineSyncLock';
const LOCK_LEASE_DURATION = 60000;
const HEARTBEAT_INTERVAL = 5000;
const lockOwnerId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

interface LockData {
  ownerId: string;
  expiresAt: number;
}

function acquireLock(): boolean {
  const now = Date.now();
  const existingLockStr = localStorage.getItem(SYNC_LOCK_KEY);
  
  if (existingLockStr) {
    try {
      const existingLock: LockData = JSON.parse(existingLockStr);
      if (existingLock.expiresAt > now && existingLock.ownerId !== lockOwnerId) {
        return false;
      }
    } catch {
      // Invalid lock data, proceed to acquire
    }
  }
  
  const newLock: LockData = {
    ownerId: lockOwnerId,
    expiresAt: now + LOCK_LEASE_DURATION,
  };
  localStorage.setItem(SYNC_LOCK_KEY, JSON.stringify(newLock));
  
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
  }
  heartbeatTimer = setInterval(() => {
    extendLease();
  }, HEARTBEAT_INTERVAL);
  
  return true;
}

function extendLease(): boolean {
  const existingLockStr = localStorage.getItem(SYNC_LOCK_KEY);
  if (!existingLockStr) return false;
  
  try {
    const existingLock: LockData = JSON.parse(existingLockStr);
    if (existingLock.ownerId !== lockOwnerId) return false;
    
    existingLock.expiresAt = Date.now() + LOCK_LEASE_DURATION;
    localStorage.setItem(SYNC_LOCK_KEY, JSON.stringify(existingLock));
    return true;
  } catch {
    return false;
  }
}

function releaseLock(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  
  const existingLockStr = localStorage.getItem(SYNC_LOCK_KEY);
  if (!existingLockStr) return;
  
  try {
    const existingLock: LockData = JSON.parse(existingLockStr);
    if (existingLock.ownerId === lockOwnerId) {
      localStorage.removeItem(SYNC_LOCK_KEY);
    }
  } catch {
    localStorage.removeItem(SYNC_LOCK_KEY);
  }
}

function getBackoffDelay(retryCount: number): number {
  const baseDelay = 1000;
  const maxDelay = 30000;
  const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);
  return delay + Math.random() * 1000;
}

export type QueuedMutationType = 
  | 'workout-log' 
  | 'workout-set'
  | 'body-weight' 
  | 'imported-workout'
  | 'program-join'
  | 'program-leave'
  | 'training-block'
  | 'training-block-update'
  | 'training-block-delete'
  | 'music-preference'
  | 'user-preference'
  | 'scheduled-session-create'
  | 'scheduled-session-update'
  | 'scheduled-session-delete';

export interface QueuedMutation {
  id: string;
  type: QueuedMutationType;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  payload: any;
  timestamp: number;
  retryCount: number;
  programId?: string;
  exerciseId?: string;
  blockId?: string;
  sessionId?: string;
}

const QUEUE_STORAGE_KEY = 'offlineQueue';
const SYNC_STATUS_KEY = 'lastSyncStatus';
const MAX_QUEUE_SIZE = 500; // Limit queue to prevent localStorage bloat on mobile

export function generateTempId(): string {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function getOfflineQueue(): QueuedMutation[] {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function saveOfflineQueue(queue: QueuedMutation[]): void {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Failed to save offline queue:', error);
  }
}

export function addToQueue(mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount'>): QueuedMutation {
  let queue = getOfflineQueue();
  
  // Enforce queue size limit - remove oldest items if queue is too large
  if (queue.length >= MAX_QUEUE_SIZE) {
    console.warn(`Offline queue at max size (${MAX_QUEUE_SIZE}), removing oldest items`);
    queue = queue.slice(-Math.floor(MAX_QUEUE_SIZE * 0.8)); // Keep newest 80%
  }
  
  const newMutation: QueuedMutation = {
    ...mutation,
    id: generateTempId(),
    timestamp: Date.now(),
    retryCount: 0,
  };
  queue.push(newMutation);
  saveOfflineQueue(queue);
  return newMutation;
}

export function removeFromQueue(id: string): void {
  const queue = getOfflineQueue();
  const filtered = queue.filter(m => m.id !== id);
  saveOfflineQueue(filtered);
}

export function updateQueueItem(id: string, updates: Partial<QueuedMutation>): void {
  const queue = getOfflineQueue();
  const index = queue.findIndex(m => m.id === id);
  if (index !== -1) {
    queue[index] = { ...queue[index], ...updates };
    saveOfflineQueue(queue);
  }
}

export function clearQueue(): void {
  localStorage.removeItem(QUEUE_STORAGE_KEY);
}

export function getQueueLength(): number {
  return getOfflineQueue().length;
}

export interface SyncResult {
  success: boolean;
  syncedCount: number;
  failedCount: number;
  errors: string[];
}

const BATCH_SIZE = 5;

async function processSingleMutation(mutation: QueuedMutation, authToken: string | null): Promise<{
  success: boolean;
  authError?: boolean;
  conflict?: boolean;
  permanentError?: boolean;
  transientError?: boolean;
  error?: string;
}> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    let response: Response;
    try {
      response = await fetch(mutation.endpoint, {
        method: mutation.method,
        headers,
        body: mutation.method !== 'DELETE' ? JSON.stringify(mutation.payload) : undefined,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (response.ok) {
      return { success: true };
    } else if (response.status === 401) {
      return { success: false, authError: true };
    } else if (response.status === 409) {
      return { success: false, conflict: true };
    } else if (response.status >= 500) {
      // Server errors are transient - should be retried
      return { success: false, transientError: true, error: `Server error (${response.status})` };
    } else if (response.status === 400 || response.status === 404 || response.status === 422) {
      // Client errors are permanent - don't retry, data may be invalid
      let errorMessage = `Request failed (${response.status})`;
      try {
        const errorBody = await response.json();
        if (errorBody.message) errorMessage = errorBody.message;
      } catch {}
      return { success: false, permanentError: true, error: errorMessage };
    } else {
      return { success: false, transientError: true, error: `HTTP ${response.status}` };
    }
  } catch (error) {
    // Network errors and timeouts are transient
    const isTimeout = error instanceof Error && error.name === 'AbortError';
    return { 
      success: false, 
      transientError: true, 
      error: isTimeout ? 'Request timeout' : 'Network error' 
    };
  }
}

export async function syncQueuedMutations(
  onProgress?: (current: number, total: number) => void
): Promise<SyncResult> {
  const queue = getOfflineQueue();
  if (queue.length === 0) {
    return { success: true, syncedCount: 0, failedCount: 0, errors: [] };
  }

  if (!acquireLock()) {
    return { success: false, syncedCount: 0, failedCount: 0, errors: ['Sync already in progress in another tab'] };
  }

  try {
    const authToken = await getAuthToken();
    if (!authToken) {
      return { success: false, syncedCount: 0, failedCount: 0, errors: ['Not authenticated'] };
    }

    let syncedCount = 0;
    let failedCount = 0;
    const errors: string[] = [];
    let authErrorOccurred = false;

    const sortedQueue = [...queue].sort((a, b) => a.timestamp - b.timestamp);
    const successfulIds: string[] = [];
    const failedIds: string[] = [];
    const MAX_RETRIES = 5;

    for (let i = 0; i < sortedQueue.length; i += BATCH_SIZE) {
      if (authErrorOccurred) break;

      const batch = sortedQueue.slice(i, i + BATCH_SIZE);
      onProgress?.(Math.min(i + BATCH_SIZE, sortedQueue.length), sortedQueue.length);

      const results = await Promise.all(
        batch.map(async (mutation) => {
          if (mutation.retryCount > 0) {
            const delay = getBackoffDelay(mutation.retryCount);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          const result = await processSingleMutation(mutation, authToken);
          return { mutation, result };
        })
      );

      for (const { mutation, result } of results) {
        if (result.success) {
          successfulIds.push(mutation.id);
          syncedCount++;
        } else if (result.authError) {
          errors.push('Authentication required. Please log in again.');
          authErrorOccurred = true;
        } else if (result.conflict) {
          successfulIds.push(mutation.id);
          console.warn(`Conflict for ${mutation.type}: server has newer data, removing from queue`);
        } else if (result.permanentError) {
          // Permanent errors (400, 404, 422) - don't retry, remove from queue
          failedIds.push(mutation.id);
          errors.push(`${mutation.type}: ${result.error || 'Invalid data'}`);
          failedCount++;
          console.warn(`Permanent error for ${mutation.type}: ${result.error}`);
        } else if (result.transientError) {
          // Transient errors (5xx, network, timeout) - retry with backoff
          if (mutation.retryCount < MAX_RETRIES) {
            updateQueueItem(mutation.id, { retryCount: mutation.retryCount + 1 });
            console.log(`Transient error for ${mutation.type}, will retry (${mutation.retryCount + 1}/${MAX_RETRIES}): ${result.error}`);
          } else {
            // After max retries, keep in queue but reset retry count for next sync cycle
            // This allows recovery when server issues are resolved
            updateQueueItem(mutation.id, { retryCount: 0 });
            errors.push(`Sync delayed for ${mutation.type}: ${result.error || 'Server temporarily unavailable'}`);
            console.warn(`Max retries reached for ${mutation.type}, will retry in next sync cycle`);
          }
        } else {
          // Unknown error type - treat as transient, reset and retry later
          if (mutation.retryCount < MAX_RETRIES) {
            updateQueueItem(mutation.id, { retryCount: mutation.retryCount + 1 });
            console.log(`Unknown error for ${mutation.type}, will retry (${mutation.retryCount + 1}/${MAX_RETRIES})`);
          } else {
            // Reset retry count instead of permanently failing - will try again later
            updateQueueItem(mutation.id, { retryCount: 0 });
            errors.push(`Sync delayed for ${mutation.type}: Will retry automatically`);
            console.warn(`Max retries reached for ${mutation.type} (unknown error), resetting for next sync cycle`);
          }
        }
      }
      
      extendLease();
    }

    if (successfulIds.length > 0 || failedIds.length > 0) {
      const currentQueue = getOfflineQueue();
      const idsToRemove = new Set([...successfulIds, ...failedIds]);
      const updatedQueue = currentQueue.filter(m => !idsToRemove.has(m.id));
      saveOfflineQueue(updatedQueue);
    }

    if (syncedCount > 0) {
      const syncedTypes = new Set(successfulIds.map(id => {
        const item = sortedQueue.find(m => m.id === id);
        return item?.type;
      }));
      
      if (syncedTypes.has('workout-log')) {
        queryClient.invalidateQueries({ queryKey: ['/api/workout-logs'] });
        queryClient.invalidateQueries({ queryKey: ['/api/workout-logs/completed'] });
        queryClient.invalidateQueries({ queryKey: ['/api/workout-logs/strength'] });
        queryClient.invalidateQueries({ queryKey: ['/api/workout-logs/cardio'] });
      }
      if (syncedTypes.has('workout-set')) {
        queryClient.invalidateQueries({ queryKey: ['/api/workout-sets'] });
        queryClient.invalidateQueries({ queryKey: ['/api/exercise-progress'] });
        queryClient.invalidateQueries({ queryKey: ['/api/exercise-latest'] });
        // Also invalidate workout logs since they're derived from the same data
        queryClient.invalidateQueries({ queryKey: ['/api/workout-logs/strength'] });
        queryClient.invalidateQueries({ queryKey: ['/api/workout-logs/cardio'] });
      }
      if (syncedTypes.has('body-weight')) {
        queryClient.invalidateQueries({ queryKey: ['/api/body-weight-logs'] });
      }
      if (syncedTypes.has('program-join') || syncedTypes.has('program-leave')) {
        queryClient.invalidateQueries({ queryKey: ['/api/user-programs'] });
      }
      if (syncedTypes.has('training-block') || syncedTypes.has('training-block-update') || syncedTypes.has('training-block-delete')) {
        queryClient.invalidateQueries({ queryKey: ['/api/training-blocks'] });
      }
      if (syncedTypes.has('music-preference')) {
        queryClient.invalidateQueries({ queryKey: ['/api/music-preferences'] });
      }
      if (syncedTypes.has('user-preference')) {
        queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      }
      if (syncedTypes.has('scheduled-session-create') || syncedTypes.has('scheduled-session-update') || syncedTypes.has('scheduled-session-delete')) {
        queryClient.invalidateQueries({ queryKey: ['/api/workout-schedule'] });
      }
    }

    const result: SyncResult = {
      success: failedCount === 0 && errors.length === 0,
      syncedCount,
      failedCount,
      errors: Array.from(new Set(errors)),
    };

    localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify({
      ...result,
      timestamp: Date.now(),
    }));

    return result;
  } finally {
    releaseLock();
  }
}

export function getLastSyncStatus(): (SyncResult & { timestamp: number }) | null {
  try {
    const stored = localStorage.getItem(SYNC_STATUS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

export interface QueueResult {
  mutation: QueuedMutation;
  wasInserted: boolean;
}

export function queueWorkoutLog(data: {
  exerciseId: string;
  programId: string;
  week: number;
  setNumber: number;
  weight?: string;
  reps?: string;
  completed?: boolean;
  distance?: number;
  durationSeconds?: number;
  paceSeconds?: number;
  distanceUnitSnapshot?: string;
  weightUnitSnapshot?: string;
  equipmentSnapshot?: string;
  barbellEntryModeSnapshot?: string;
  exerciseNameSnapshot?: string; // Exercise name for progress tracking
  programNameSnapshot?: string; // Program name for data preservation
  sectionNameSnapshot?: string; // Section name for context
  loggedAt?: string; // ISO date string for the workout date
  isWarmup?: boolean; // Whether this is a warm-up set (skip RIR tracking)
}): QueueResult {
  const queue = getOfflineQueue();
  
  // Find existing entry for this set (upsert behavior)
  const existingIndex = queue.findIndex(m => {
    if (m.type !== 'workout-log') return false;
    const p = m.payload;
    return p.programId === data.programId &&
           p.exerciseId === data.exerciseId &&
           p.week === data.week &&
           p.setNumber === data.setNumber;
  });
  
  const newPayload = {
    ...data,
    clientTimestamp: Date.now(),
  };
  
  if (existingIndex !== -1) {
    // Update existing entry with new payload (also reset retryCount)
    queue[existingIndex].payload = newPayload;
    queue[existingIndex].timestamp = Date.now();
    queue[existingIndex].retryCount = 0;
    saveOfflineQueue(queue);
    return { mutation: queue[existingIndex], wasInserted: false };
  }
  
  // No existing entry, add new one
  const mutation = addToQueue({
    type: 'workout-log',
    endpoint: '/api/workout-logs',
    method: 'POST',
    payload: newPayload,
    programId: data.programId,
    exerciseId: data.exerciseId,
  });
  return { mutation, wasInserted: true };
}

export function queueWorkoutSet(data: {
  exerciseId: string;
  exerciseSource: 'master' | 'user';
  exerciseName: string;
  programId?: string;
  programName?: string;
  sessionName?: string;
  workoutInstanceId?: string;
  workoutDate: string;
  setNumber: number;
  equipment: string;
  weight?: number;
  weightUnit?: string;
  reps?: number;
  targetReps?: string;
  completed?: boolean;
  rpe?: number;
  rir?: number;
  isWarmup?: boolean; // Whether this is a warm-up set (skip RIR tracking)
  tempo?: string;
  distance?: number;
  distanceUnit?: string;
  durationSeconds?: number;
  paceSeconds?: number;
  holdSeconds?: number;
  notes?: string;
  barbellEntryMode?: string;
}): QueueResult {
  const queue = getOfflineQueue();
  
  // Dedup key: Find any existing entry for this exercise/date/set/program combo
  // We intentionally DO NOT include workoutInstanceId in the dedup because:
  // 1. The instanceId might be created AFTER the first set is logged
  // 2. We want to UPDATE the same set entry even if instanceId changes
  // 3. The backend will handle dedup with the latest workoutInstanceId
  const existingIndex = queue.findIndex(m => {
    if (m.type !== 'workout-set') return false;
    const p = m.payload;
    return p.exerciseId === data.exerciseId &&
           p.workoutDate === data.workoutDate &&
           p.setNumber === data.setNumber &&
           p.programId === data.programId;
  });
  
  const newPayload = {
    ...data,
    clientTimestamp: Date.now(),
  };
  
  if (existingIndex !== -1) {
    queue[existingIndex].payload = newPayload;
    queue[existingIndex].timestamp = Date.now();
    queue[existingIndex].retryCount = 0;
    saveOfflineQueue(queue);
    return { mutation: queue[existingIndex], wasInserted: false };
  }
  
  const mutation = addToQueue({
    type: 'workout-set',
    endpoint: '/api/workout-sets',
    method: 'POST',
    payload: newPayload,
    programId: data.programId,
    exerciseId: data.exerciseId,
  });
  return { mutation, wasInserted: true };
}

export function removeQueuedWorkoutSet(
  exerciseId: string,
  workoutDate: string,
  setNumber: number,
  programId?: string | null
): boolean {
  const queue = getOfflineQueue();
  const filtered = queue.filter(m => {
    if (m.type !== 'workout-set') return true;
    const payload = m.payload;
    const programIdMatches = (payload.programId ?? null) === (programId ?? null);
    return !(
      payload.exerciseId === exerciseId &&
      payload.workoutDate === workoutDate &&
      payload.setNumber === setNumber &&
      programIdMatches
    );
  });
  
  if (filtered.length < queue.length) {
    saveOfflineQueue(filtered);
    return true;
  }
  return false;
}

export function queueBodyWeightLog(data: {
  weight: number;
  loggedAt?: string;
}): QueuedMutation {
  const queue = getOfflineQueue();
  const logDate = data.loggedAt ? new Date(data.loggedAt) : new Date();
  const logDateStr = logDate.toISOString().split('T')[0];
  
  const existingIndex = queue.findIndex(m => {
    if (m.type !== 'body-weight') return false;
    const payloadDate = m.payload.loggedAt 
      ? new Date(m.payload.loggedAt).toISOString().split('T')[0]
      : new Date(m.timestamp).toISOString().split('T')[0];
    return payloadDate === logDateStr;
  });
  
  const newPayload = {
    ...data,
    clientTimestamp: Date.now(),
  };
  
  if (existingIndex !== -1) {
    queue[existingIndex].payload = newPayload;
    queue[existingIndex].timestamp = Date.now();
    queue[existingIndex].retryCount = 0;
    saveOfflineQueue(queue);
    return queue[existingIndex];
  }
  
  return addToQueue({
    type: 'body-weight',
    endpoint: '/api/body-weight-logs',
    method: 'POST',
    payload: newPayload,
  });
}

export function removeQueuedWorkoutLog(
  programId: string,
  exerciseId: string,
  week: number,
  setNumber: number
): boolean {
  const queue = getOfflineQueue();
  const filtered = queue.filter(m => {
    if (m.type !== 'workout-log') return true;
    const payload = m.payload;
    return !(
      payload.programId === programId &&
      payload.exerciseId === exerciseId &&
      payload.week === week &&
      payload.setNumber === setNumber
    );
  });
  if (filtered.length !== queue.length) {
    saveOfflineQueue(filtered);
    return true;
  }
  return false;
}

export function queueProgramJoin(data: {
  programId: string;
  programName: string;
  startDate?: string;
  status?: string;
  isFavorite?: boolean;
  source?: string;
}): QueuedMutation {
  const queue = getOfflineQueue();
  
  const existingLeaveIndex = queue.findIndex(m => 
    m.type === 'program-leave' && m.programId === data.programId
  );
  if (existingLeaveIndex !== -1) {
    queue.splice(existingLeaveIndex, 1);
    saveOfflineQueue(queue);
  }
  
  const existingJoinIndex = queue.findIndex(m =>
    m.type === 'program-join' && m.programId === data.programId
  );
  if (existingJoinIndex !== -1) {
    queue[existingJoinIndex].payload = {
      ...queue[existingJoinIndex].payload,
      status: data.status || queue[existingJoinIndex].payload.status || 'current',
      isFavorite: data.isFavorite ?? queue[existingJoinIndex].payload.isFavorite ?? false,
      source: data.source || queue[existingJoinIndex].payload.source || 'expert',
      clientTimestamp: Date.now(),
    };
    queue[existingJoinIndex].timestamp = Date.now();
    saveOfflineQueue(queue);
    return queue[existingJoinIndex];
  }
  
  return addToQueue({
    type: 'program-join',
    endpoint: `/api/user-programs/membership/${data.programId}`,
    method: 'PUT',
    payload: { 
      programName: data.programName,
      startDate: data.startDate,
      status: data.status || 'current',
      isFavorite: data.isFavorite ?? false,
      source: data.source || 'expert',
      clientTimestamp: Date.now()
    },
    programId: data.programId,
  });
}

export function queueProgramLeave(programId: string): QueuedMutation | null {
  const queue = getOfflineQueue();
  
  const existingJoinIndex = queue.findIndex(m =>
    m.type === 'program-join' && m.programId === programId
  );
  if (existingJoinIndex !== -1) {
    queue.splice(existingJoinIndex, 1);
    saveOfflineQueue(queue);
    return null;
  }
  
  const existingLeaveIndex = queue.findIndex(m =>
    m.type === 'program-leave' && m.programId === programId
  );
  if (existingLeaveIndex !== -1) {
    return queue[existingLeaveIndex];
  }
  
  return addToQueue({
    type: 'program-leave',
    endpoint: `/api/user-programs/membership/${programId}`,
    method: 'DELETE',
    payload: {},
    programId,
  });
}

export function queueTrainingBlock(data: {
  name: string;
  programId: string;
  category: string;
  startDate: string;
  endDate: string;
  notes?: string;
}, tempBlockId?: string): QueuedMutation {
  const blockId = tempBlockId || `temp_block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return addToQueue({
    type: 'training-block',
    endpoint: '/api/training-blocks',
    method: 'POST',
    payload: { ...data, clientTimestamp: Date.now() },
    programId: data.programId,
    blockId,
  });
}

export function queueTrainingBlockUpdate(
  blockId: string,
  data: Partial<{
    name: string;
    category: string;
    startDate: string;
    endDate: string;
    notes: string;
  }>
): QueuedMutation {
  const queue = getOfflineQueue();
  
  const createIndex = queue.findIndex(m =>
    m.type === 'training-block' && m.blockId === blockId
  );
  if (createIndex !== -1) {
    queue[createIndex].payload = { ...queue[createIndex].payload, ...data, clientTimestamp: Date.now() };
    queue[createIndex].timestamp = Date.now();
    saveOfflineQueue(queue);
    return queue[createIndex];
  }
  
  const existingIndex = queue.findIndex(m =>
    (m.type === 'training-block-update') && m.blockId === blockId
  );
  
  if (existingIndex !== -1) {
    queue[existingIndex].payload = { ...queue[existingIndex].payload, ...data, clientTimestamp: Date.now() };
    queue[existingIndex].timestamp = Date.now();
    queue[existingIndex].retryCount = 0;
    saveOfflineQueue(queue);
    return queue[existingIndex];
  }
  
  return addToQueue({
    type: 'training-block-update',
    endpoint: `/api/training-blocks/${blockId}`,
    method: 'PATCH',
    payload: { ...data, clientTimestamp: Date.now() },
    blockId,
  });
}

export function queueTrainingBlockDelete(blockId: string): QueuedMutation | null {
  const queue = getOfflineQueue();
  
  const createIndex = queue.findIndex(m =>
    m.type === 'training-block' && m.blockId === blockId
  );
  if (createIndex !== -1) {
    queue.splice(createIndex, 1);
    saveOfflineQueue(queue);
    return null;
  }
  
  const updateIndices = queue.reduce<number[]>((acc, m, i) => {
    if (m.type === 'training-block-update' && m.blockId === blockId) {
      acc.push(i);
    }
    return acc;
  }, []);
  for (let i = updateIndices.length - 1; i >= 0; i--) {
    queue.splice(updateIndices[i], 1);
  }
  saveOfflineQueue(queue);
  
  return addToQueue({
    type: 'training-block-delete',
    endpoint: `/api/training-blocks/${blockId}`,
    method: 'DELETE',
    payload: {},
    blockId,
  });
}

export function queueMusicPreference(data: {
  programId: string;
  sessionId?: string;
  spotifyPlaylistUri?: string | null;
  spotifyPlaylistName?: string | null;
  customPlaylistUrl?: string | null;
  preferenceType?: string;
}): QueuedMutation {
  const queue = getOfflineQueue();
  
  // Build the API payload with proper scopeType and compound scopeId
  // sessionId here is the compound key (programId:sessionId) from music-settings
  const isSession = !!data.sessionId;
  const apiPayload = {
    scopeType: isSession ? 'session' : 'program',
    scopeId: data.sessionId || data.programId,
    programId: isSession ? data.programId : undefined,
    mainMode: data.preferenceType || 'custom',
    mainPlaylistUrl: data.customPlaylistUrl,
    warmupMode: 'curated',
    warmupPlaylistUrl: '',
    cooldownMode: 'curated',
    cooldownPlaylistUrl: '',
    clientTimestamp: Date.now(),
  };
  
  // Match on sessionId if provided, otherwise match on programId
  const existingIndex = queue.findIndex(m =>
    m.type === 'music-preference' && 
    (data.sessionId 
      ? m.sessionId === data.sessionId 
      : m.programId === data.programId && !m.sessionId)
  );
  
  if (existingIndex !== -1) {
    queue[existingIndex].payload = apiPayload;
    queue[existingIndex].timestamp = Date.now();
    queue[existingIndex].retryCount = 0;
    saveOfflineQueue(queue);
    return queue[existingIndex];
  }
  
  return addToQueue({
    type: 'music-preference',
    endpoint: '/api/music-preferences',
    method: 'POST',
    payload: apiPayload,
    programId: data.programId,
    sessionId: data.sessionId,
  });
}

export function queueUserPreference(data: Record<string, any>): QueuedMutation {
  const queue = getOfflineQueue();
  
  const existingIndex = queue.findIndex(m => m.type === 'user-preference');
  
  if (existingIndex !== -1) {
    queue[existingIndex].payload = { ...queue[existingIndex].payload, ...data, clientTimestamp: Date.now() };
    queue[existingIndex].timestamp = Date.now();
    queue[existingIndex].retryCount = 0;
    saveOfflineQueue(queue);
    return queue[existingIndex];
  }
  
  return addToQueue({
    type: 'user-preference',
    endpoint: '/api/user/preferences',
    method: 'PATCH',
    payload: { ...data, clientTimestamp: Date.now() },
  });
}

export interface ScheduledSessionPayload {
  id?: string;
  tempId?: string;
  programId: string;
  programName?: string;
  week: number;
  workoutIndex: number;
  workoutName: string;
  scheduledDate: string;
}

export function queueScheduledSessionCreate(data: ScheduledSessionPayload): QueuedMutation {
  const tempId = data.tempId || generateTempId();
  const queue = getOfflineQueue();
  
  const existingIndex = queue.findIndex(m =>
    m.type === 'scheduled-session-create' && m.sessionId === tempId
  );
  
  if (existingIndex !== -1) {
    queue[existingIndex].payload = { ...data, tempId };
    queue[existingIndex].timestamp = Date.now();
    queue[existingIndex].retryCount = 0;
    saveOfflineQueue(queue);
    return queue[existingIndex];
  }
  
  return addToQueue({
    type: 'scheduled-session-create',
    endpoint: '/api/workout-schedule',
    method: 'POST',
    payload: { ...data, tempId },
    programId: data.programId,
    sessionId: tempId,
  });
}

export function queueScheduledSessionUpdate(data: { id: string; scheduledDate: string }): QueuedMutation {
  const queue = getOfflineQueue();
  
  const existingIndex = queue.findIndex(m =>
    (m.type === 'scheduled-session-update' && m.sessionId === data.id) ||
    (m.type === 'scheduled-session-create' && m.sessionId === data.id)
  );
  
  if (existingIndex !== -1) {
    if (queue[existingIndex].type === 'scheduled-session-create') {
      queue[existingIndex].payload.scheduledDate = data.scheduledDate;
    } else {
      queue[existingIndex].payload = data;
    }
    queue[existingIndex].timestamp = Date.now();
    queue[existingIndex].retryCount = 0;
    saveOfflineQueue(queue);
    return queue[existingIndex];
  }
  
  return addToQueue({
    type: 'scheduled-session-update',
    endpoint: `/api/workout-schedule/${data.id}`,
    method: 'PUT',
    payload: { scheduledDate: data.scheduledDate },
    sessionId: data.id,
  });
}

export function queueScheduledSessionDelete(sessionId: string): QueuedMutation | null {
  const queue = getOfflineQueue();
  
  const createIndex = queue.findIndex(m =>
    m.type === 'scheduled-session-create' && m.sessionId === sessionId
  );
  
  if (createIndex !== -1) {
    queue.splice(createIndex, 1);
    saveOfflineQueue(queue);
    return null;
  }
  
  const updateIndex = queue.findIndex(m =>
    m.type === 'scheduled-session-update' && m.sessionId === sessionId
  );
  
  if (updateIndex !== -1) {
    queue.splice(updateIndex, 1);
    saveOfflineQueue(queue);
  }
  
  return addToQueue({
    type: 'scheduled-session-delete',
    endpoint: `/api/workout-schedule/${sessionId}`,
    method: 'DELETE',
    payload: {},
    sessionId,
  });
}
